(() => {
  if (customElements.get('gapasea-video-reviews')) return;

  class GapaseaVideoReviews extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const { signal } = this.events;
      this.dialog = this.querySelector('dialog');
      this.opener = this.querySelector('[data-review-open]');
      this.tabs = [...this.querySelectorAll('[data-review-tab]')];
      this.modeTabs = [...this.querySelectorAll('[data-mode-tab]')];
      if (!this.dialog || !this.opener || !this.dialog.showModal) return;

      this.opener.hidden = false;
      this.opener.addEventListener('click', () => this.open(), { signal });
      this.querySelector('[data-review-close]').addEventListener('click', () => this.dialog.close(), { signal });
      this.dialog.addEventListener('close', () => this.onClose(), { signal });
      this.dialog.addEventListener('click', (event) => {
        const rect = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) {
          this.dialog.close();
        }
      }, { signal });

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(tab), { signal });
        tab.addEventListener('keydown', (event) => {
          let next;
          if (event.key === 'ArrowDown') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowUp') next = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.tabs[next].focus();
          this.select(this.tabs[next], false);
        }, { signal });
      });

      this.modeTabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.selectMode(tab), { signal });
        tab.addEventListener('keydown', (event) => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.modeTabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + this.modeTabs.length) % this.modeTabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.modeTabs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.modeTabs[next].focus();
          this.selectMode(this.modeTabs[next]);
        }, { signal });
      });

      this.querySelectorAll('[data-review-play]').forEach((button) => {
        button.addEventListener('click', () => this.play(button), { signal });
      });

      // Keep sections usable when Shopify's editor reloads or selects a block.
      this.addEventListener('shopify:block:select', (event) => {
        const tab = this.tabs.find((item) => item.dataset.reviewTab === event.detail.blockId);
        const videoMode = this.modeTabs.find((item) => item.dataset.modeTab === 'video');
        const customerMode = this.modeTabs.find((item) => item.dataset.modeTab === 'customer');
        if (tab) {
          this.open();
          if (videoMode) this.selectMode(videoMode);
          this.select(tab);
        } else if (customerMode) {
          // Any non-review block in this section is the app block on the customer tab.
          this.open();
          this.selectMode(customerMode);
        }
      }, { signal });
    }

    open() {
      if (this.dialog.open) return;
      document.dispatchEvent(new CustomEvent('gapasea:video-reviews-open'));
      this.dialog.showModal();
      this.classList.add('is-open');
      this.opener.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('gapasea-reviews-open');
      const body = this.querySelector('[data-mode-panel]:not([hidden]) .gr__body, .gr__body');
      if (body) body.scrollTop = 0;
    }

    selectMode(selected) {
      // Leaving the video tab must not leave a video playing behind the panel.
      this.stop();
      this.modeTabs.forEach((tab) => {
        const active = tab === selected;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      this.querySelectorAll('[data-mode-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.modePanel !== selected.dataset.modeTab;
      });
      const shown = this.querySelector('[data-mode-panel]:not([hidden])');
      const scroller = shown?.querySelector('.gr__body, .gr__loox');
      if (scroller) scroller.scrollTop = 0;
    }

    select(selected, scroll = true) {
      this.stop();
      this.tabs.forEach((tab) => {
        const active = tab === selected;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      this.querySelectorAll('[data-review-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.reviewPanel !== selected.dataset.reviewTab;
      });
      if (scroll && matchMedia('(max-width: 899px)').matches) {
        this.querySelector('.gr__body')?.scrollTo({ top: 0, behavior: 'instant' });
        this.querySelector('[data-review-panel]:not([hidden]) [data-review-play]')?.focus({ preventScroll: true });
      }
    }

    play(button) {
      const id = button.dataset.videoId;
      if (!/^[\w-]{11}$/.test(id)) return;
      const player = button.parentElement.querySelector('[data-review-player]');
      const frame = document.createElement('iframe');
      frame.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&playsinline=1`;
      frame.title = button.getAttribute('aria-label');
      frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      frame.allowFullscreen = true;
      frame.referrerPolicy = 'strict-origin-when-cross-origin';
      player.replaceChildren(frame);
      player.hidden = false;
      button.hidden = true;
      frame.focus();
    }

    stop() {
      this.querySelectorAll('[data-review-player]').forEach((player) => {
        player.replaceChildren();
        player.hidden = true;
      });
      this.querySelectorAll('[data-review-play]').forEach((button) => { button.hidden = false; });
    }

    onClose() {
      this.stop();
      this.classList.remove('is-open');
      this.opener.setAttribute('aria-expanded', 'false');
      if (!document.querySelector('gapasea-video-reviews dialog[open]')) {
        document.documentElement.classList.remove('gapasea-reviews-open');
      }
      this.opener.focus({ preventScroll: true });
    }

    disconnectedCallback() {
      this.events?.abort();
      this.stop();
      if (this.dialog?.open) this.dialog.close();
      if (!document.querySelector('gapasea-video-reviews dialog[open]')) {
        document.documentElement.classList.remove('gapasea-reviews-open');
      }
    }
  }

  customElements.define('gapasea-video-reviews', GapaseaVideoReviews);
})();
